import { type FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useUpdateContentManyMutation } from "@/partner/entities/contents";
import { useProductsQuery } from "@/partner/entities/products";
import { ProductStatusType } from "@/types/graphql/partner.generated";

// Трёхсостояние чекбокса продукта относительно выбранных элементов (порт
// референсного FULL/PART/NONE — тот же приём, что и в AddToCollectionButton)
const FULL = "FULL";
const PART = "PART";
const NONE = "NONE";
type CheckboxState = typeof FULL | typeof PART | typeof NONE;

export interface AddToProductItem {
  id: string;
  productIds: string[];
}

interface AddToProductButtonProps {
  selectedItems: AddToProductItem[];
  onCompleted: () => void;
  disabled?: boolean;
  /** Рендер как пункт меню «Действия» (иначе — кнопка) */
  menuItem?: boolean;
  /** Списки, которые нужно рефетчить после привязки */
  refetchQueries: string[];
}

// Порт референсного AddToProductButton: меню 400×400 со списком чекбоксов
// продуктов (FULL/PART/NONE по выбранным элементам); сохранение — та же
// merge-логика, что и в AddToCollectionButton (PART остаются, FULL
// добавляются), через общую updateContentMany.
export const AddToProductButton: FC<AddToProductButtonProps> = ({
  selectedItems,
  onCompleted,
  disabled = false,
  menuItem = false,
  refetchQueries,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [checkboxState, setCheckboxState] = useState<
    Record<string, CheckboxState>
  >({});

  const { data } = useProductsQuery({
    skip: !open,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    // ALL — тот же смысл, что и includeIds в референсе: партнёр должен
    // видеть и завершившиеся/черновые продукты, к которым уже привязан
    // контент, а не только «активные» (дефолт products()).
    variables: {
      page: 0,
      rowsPerPage: 1000,
      status: ProductStatusType.AllType,
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });
  const products = useMemo(
    () => data?.products?.nodes ?? [],
    [data?.products?.nodes]
  );

  useEffect(() => {
    setCheckboxState(() =>
      products.reduce<Record<string, CheckboxState>>((all, product) => {
        const full = selectedItems.every((item) =>
          (item.productIds ?? []).includes(product.id)
        );
        const part = selectedItems.some((item) =>
          (item.productIds ?? []).includes(product.id)
        );
        all[product.id] = full ? FULL : part ? PART : NONE;
        return all;
      }, {})
    );
  }, [selectedItems, products]);

  const [updateContentMany, { loading: saving }] = useUpdateContentManyMutation(
    {
      refetchQueries,
      onCompleted: () => {
        setAnchorEl(null);
        enqueueSnackbar(t("products.addButton.contentsUpdated"), {
          variant: "success",
        });
        onCompleted();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    }
  );

  const onClickListItem = (productId: string) => {
    setCheckboxState((prev) => ({
      ...prev,
      // FULL → NONE, PART/NONE → FULL (порт клика по строке)
      [productId]: prev[productId] === FULL ? NONE : FULL,
    }));
  };

  const onClickSave = () => {
    // Merge-логика референса: частично отмеченные (PART) элементы сохраняют
    // свои привязки, FULL добавляется всем, снятые (NONE) — убираются.
    const input = selectedItems.map((item) => {
      const productIds = (item.productIds ?? [])
        .filter((productId) => checkboxState[productId] === PART)
        .concat(
          Object.keys(checkboxState).filter(
            (productId) => checkboxState[productId] === FULL
          )
        );
      return { id: item.id, productIds };
    });
    updateContentMany({ variables: { input } });
  };

  const trigger = menuItem ? (
    <MenuItem
      onClick={(event) => setAnchorEl(event.currentTarget)}
      disabled={disabled}
    >
      {t("products.addButton.button")}
    </MenuItem>
  ) : (
    <Button
      color="inherit"
      onClick={(event) => setAnchorEl(event.currentTarget)}
      disabled={disabled}
    >
      {t("products.addButton.button")}
    </Button>
  );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 400, maxWidth: "100%" } } }}
      >
        <Grid
          container
          sx={{ px: 2, py: 1, bgcolor: "background.default" }}
          alignItems="center"
        >
          <Grid size={7}>
            <Typography variant="h6">
              {t("products.addButton.select")}
            </Typography>
          </Grid>
          <Grid size={5} sx={{ textAlign: "right" }}>
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="small"
              onClick={onClickSave}
              disabled={saving}
            >
              {t("common.actions.save")}
            </Button>
          </Grid>
        </Grid>

        <Box
          component="ul"
          sx={{
            m: 0,
            p: 0,
            listStyle: "none",
            overflowY: "auto",
            height: 347,
          }}
        >
          {products.map((product) => {
            const state = checkboxState[product.id] ?? NONE;
            return (
              <ListItemButton
                key={product.id}
                dense
                onClick={() => onClickListItem(product.id)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={state === FULL}
                    indeterminate={state === PART}
                    tabIndex={-1}
                    color="default"
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={product.title}
                  primaryTypographyProps={{
                    color: state === NONE ? "textSecondary" : "textPrimary",
                  }}
                />
              </ListItemButton>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};
